import { TrendingUp, UserRound, Users } from "@/components/icons";
import { Skeleton } from "@/components/kit";
import type { CustomerReportSummaryDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { ReportTermHint } from "./ReportTermHint";

type CustomerReportsOverviewStripProps = {
  summary?: CustomerReportSummaryDTO;
  isLoading?: boolean;
};

export function CustomerReportsOverviewStrip({
  summary,
  isLoading,
}: CustomerReportsOverviewStripProps) {
  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {["a", "b", "c", "d"].map((k) => (
          <Skeleton key={k} className="h-22 rounded-2xl" />
        ))}
      </div>
    );
  }

  const codSharePct = ((summary?.codOrderShare ?? 0) * 100).toFixed(1);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="brand-card flex items-center gap-3 p-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-secondary/15 text-secondary">
            <Users />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-muted-foreground text-xs">ລູກຄ້າທີ່ຊື້</span>
            <span className="font-display text-2xl tabular-nums leading-tight">
              {summary?.activeCustomers ?? 0}
            </span>
            <span className="text-muted-foreground text-xs">
              ມີອໍເດີ້ຢືນຢັນໃນຊ່ວງ
            </span>
          </div>
        </div>

        <div className="brand-card flex items-center gap-3 p-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent/40 text-foreground">
            <UserRound />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <ReportTermHint
              label="ລູກຄ້າໃໝ່"
              hint="ອໍເດີ້ຢືນຢັນຄັ້ງແຮກຢູ່ໃນຊ່ວງນີ້ — ບໍ່ມີອໍເດີ້ຢືນຢັນກ່ອນວັນເລີ່ມ"
              className="text-muted-foreground text-xs underline decoration-dotted underline-offset-2"
            />
            <span className="font-display text-2xl tabular-nums leading-tight">
              {summary?.newCustomers ?? 0}
            </span>
            <span className="text-muted-foreground text-xs">
              ຊື້ຊ້ຳ {summary?.returningCustomers ?? 0}
            </span>
          </div>
        </div>

        <div className="brand-card flex items-center gap-3 p-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <TrendingUp />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-muted-foreground text-xs">ຍອດສະເລ່ຍ/ຄົນ</span>
            <span className="font-display text-xl tabular-nums leading-tight sm:text-2xl">
              {formatLak(summary?.avgRevenuePerCustomer ?? "0")}
            </span>
            <span className="text-muted-foreground text-xs">
              {summary?.avgOrdersPerCustomer ?? 0} ອໍເດີ້/ຄົນ
            </span>
          </div>
        </div>

        <div className="brand-card flex items-center gap-3 p-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
            <TrendingUp />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-muted-foreground text-xs">COD ຕີກັບ</span>
            <span className="font-display text-2xl tabular-nums leading-tight">
              {summary?.customersWithCodReturn ?? 0}
            </span>
            <span className="text-muted-foreground text-xs">
              ອໍເດີ້ COD {codSharePct}% · ລູກຄ້າທີ່ມີຕີກັບ
            </span>
          </div>
        </div>
      </div>
      <p className="text-muted-foreground text-xs">
        <ReportTermHint
          label="ຊື້ຊ້ຳ"
          hint="ມີອໍເດີ້ຢືນຢັນໃນຊ່ວງ ແລະ ມີອໍເດີ້ຢືນຢັນກ່ອນວັນເລີ່ມ — ບໍ່ສັບກັບ orderCount ≥ 2 ໃນໂປຣໄຟລ໌"
        />{" "}
        · ຍອດຂາຍ = ອໍເດີ້ຢືນຢັນ (paid → delivered) ຕາມວັນສ້າງ
      </p>
    </div>
  );
}
